import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * One-shot recovery-code display modal.
 * Props:
 *   code: string (plaintext code from server)
 *   variant: "signup" | "reset" (title/body copy)
 *   onClose: () => void  (called after user acknowledges)
 */
export default function RecoveryCodeModal({ code, variant = "signup", onClose }) {
  const { t } = useTranslation();
  const [ack, setAck] = useState(false);

  if (!code) return null;

  const title =
    variant === "reset" ? t("recovery.reset_modal_title") : t("recovery.modal_title");
  const body =
    variant === "reset" ? t("recovery.reset_modal_body") : t("recovery.body");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("recovery.copied"));
    } catch (_) {
      // Fallback: select-and-copy via a hidden input.
      const el = document.createElement("textarea");
      el.value = code;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); toast.success(t("recovery.copied")); } catch (__) { /* ignore */ }
      document.body.removeChild(el);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="recovery-code-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900" data-testid="recovery-code-title">
          {title}
        </h2>
        <p className="mt-2 text-sm text-slate-600">{body}</p>

        <div
          className="mt-5 flex flex-col items-center rounded-xl border border-indigo-100 bg-indigo-50 p-4"
        >
          <code
            className="select-all font-mono text-lg font-semibold tracking-widest text-indigo-900"
            data-testid="recovery-code-value"
          >
            {code}
          </code>
          <button
            type="button"
            onClick={copy}
            className="mt-3 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
            data-testid="recovery-code-copy-btn"
          >
            {t("recovery.copy")}
          </button>
        </div>

        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
          ⚠ {t("recovery.warning")}
        </div>

        <label
          className="mt-4 flex items-start gap-2 text-sm text-slate-700"
          data-testid="recovery-code-ack-label"
        >
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            data-testid="recovery-code-ack-checkbox"
          />
          <span>{t("recovery.ack_label")}</span>
        </label>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={!ack}
            onClick={onClose}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="recovery-code-close-btn"
          >
            {t("recovery.close_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}
